import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaBoxOpen, FaTimes } from "react-icons/fa";
import { catalogApi } from "../api/services";
import { resolveAssetUrl } from "../utils/assets";
import { buildCategoryTree, flattenCategoryOptions } from "../utils/categories";

const HeaderSearch = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    catalogApi
      .getCategories()
      .then((res) => {
        const tree = buildCategoryTree(res.data.data.categories || []);
        setCategories(flattenCategoryOptions(tree));
      })
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!category) {
      setCategoryQuery("");
      return;
    }
    const match = categories.find((c) => c.slug === category);
    if (match) setCategoryQuery(match.shortLabel || match.name);
  }, [category, categories]);

  useEffect(() => {
    const q = searchTerm.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { search: q, limit: 8, page: 1 };
        if (category) params.category = category;
        const res = await catalogApi.getProducts(params);
        setSuggestions(res.data.data.products || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, category]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.slug === category) || null,
    [category, categories]
  );

  const selectedLabel = selectedCategory?.shortLabel || selectedCategory?.name || "All Categories";

  const filteredCategories = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    if (!q) return categories.slice(0, 50);
    return categories
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.label.toLowerCase().includes(q) ||
          c.slug?.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [categories, categoryQuery]);

  const selectCategory = (slug) => {
    setCategory(slug);
    setCategoryOpen(false);
    if (!slug) setCategoryQuery("");
  };

  const goSearch = (e) => {
    e?.preventDefault?.();
    const q = searchTerm.trim();
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    if (category) params.set("category", category);
    if (!q && !category) {
      navigate("/products");
      onNavigate?.();
      return;
    }
    setOpen(false);
    onNavigate?.();
    navigate(`/products?${params.toString()}`);
  };

  const goProduct = (product) => {
    setOpen(false);
    setSearchTerm("");
    onNavigate?.();
    navigate(`/product/${product.slug || product.id}`);
  };

  return (
    <div className="top-search-wrap" ref={wrapRef}>
      <form className="top-search" onSubmit={goSearch}>
        <div className="top-search-category-wrap">
          <div className="top-search-category">
            <input
              type="text"
              className="top-search-category-input"
              aria-label="Search category"
              placeholder="All Categories"
              value={categoryQuery}
              title={selectedCategory?.label || "All Categories"}
              onChange={(e) => {
                setCategoryQuery(e.target.value);
                setCategory("");
                setCategoryOpen(true);
                setOpen(false);
              }}
              onFocus={() => {
                setCategoryOpen(true);
                setOpen(false);
              }}
              autoComplete="off"
            />
            {category && (
              <button
                type="button"
                className="top-search-category-clear"
                aria-label="Clear category"
                onClick={() => selectCategory("")}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <input
          type="text"
          className="top-search-query"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            setCategoryOpen(false);
            if (suggestions.length || searchTerm.trim().length >= 2) setOpen(true);
          }}
          autoComplete="off"
        />

        <button type="submit" className="top-search-submit" aria-label="Search">
          <FaSearch />
        </button>
      </form>

      {categoryOpen && (
        <div className="top-search-category-menu" role="listbox">
          <button
            type="button"
            role="option"
            className={`top-search-category-option ${!category ? "active" : ""}`}
            onClick={() => selectCategory("")}
          >
            All Categories
          </button>
          {filteredCategories.length === 0 ? (
            <div className="top-search-category-empty">No matching categories</div>
          ) : (
            filteredCategories.map((c) => (
              <button
                type="button"
                role="option"
                key={c.slug}
                title={c.label}
                className={`top-search-category-option ${
                  c.slug === category ? "active" : ""
                }`}
                onClick={() => selectCategory(c.slug)}
              >
                <span
                  className="top-search-category-name"
                  style={{ paddingLeft: `${(c.depth || 0) * 12}px` }}
                >
                  {c.name}
                </span>
                {c.depth > 0 && c.path?.length > 1 && (
                  <small className="top-search-category-path">
                    {c.path.slice(0, -1).join(" › ")}
                  </small>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {open && searchTerm.trim().length >= 2 && (
        <div className="search-suggest">
          {loading && <div className="search-suggest-status">Searching...</div>}
          {!loading && suggestions.length === 0 && (
            <div className="search-suggest-status">No products found</div>
          )}
          {!loading &&
            suggestions.map((product) => {
              const image = resolveAssetUrl(product.image || product.images?.[0]);
              const categoryName =
                typeof product.category === "object"
                  ? product.category?.name
                  : product.category;
              return (
                <button
                  type="button"
                  key={product.id}
                  className="search-suggest-item"
                  onClick={() => goProduct(product)}
                >
                  <div className="search-suggest-thumb">
                    {image ? <img src={image} alt="" /> : <FaBoxOpen />}
                  </div>
                  <div className="search-suggest-meta">
                    <strong>{product.name}</strong>
                    <span>
                      {categoryName || "Product"} · AED{" "}
                      {Number(product.price || 0).toFixed(2)}
                    </span>
                  </div>
                </button>
              );
            })}
          {!loading && suggestions.length > 0 && (
            <button type="button" className="search-suggest-all" onClick={goSearch}>
              View all results
              {category ? ` in ${selectedLabel}` : ""} for “{searchTerm.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
